/**
 * Rendering money without ever making it a number — I2, at the last mile.
 *
 * The invariant says integer minor units plus an ISO currency code, with no `number` type
 * for money anywhere. It is tempting to treat the browser as exempt: it only *displays*
 * the value. It is not exempt. `Number('89500000001') / 100` is already past the range
 * where a double is exact once fees are added, and 89 500 000 001 minor units is an
 * everyday LBP figure rather than a contrived one.
 *
 * So the split is done on the **string**: take the last N digits as the fraction and the
 * rest as the integer part, then group the integer part by hand. No arithmetic happens.
 *
 * Digits stay Latin throughout, including in Arabic — `i18n/index.ts` explains why: these
 * numbers are read aloud between a Courier and a dispatcher and typed into a search box.
 */

/** Minor units per major unit. LBP and SYP are quoted whole; USD has cents. */
const EXPONENT: Readonly<Record<string, number>> = {
  USD: 2,
  // Lebanon and Syria quote whole units in practice — there is no circulating subdivision
  // of either. Storing them with exponent 0 keeps `minor` and the amount people say aloud
  // the same number, which is what a Courier counting cash at a door actually needs.
  LBP: 0,
  SYP: 0,
};

function group(digits: string): string {
  // Thousands separators inserted from the right, on the string. `Intl.NumberFormat`
  // would be the obvious tool and takes a `number` — which is the one thing that must not
  // happen here.
  const out: string[] = [];
  for (let i = digits.length; i > 0; i -= 3) {
    out.unshift(digits.slice(Math.max(0, i - 3), i));
  }
  return out.join(',');
}

/**
 * `formatMinor('89500000001', 'LBP')` → `"89,500,000,001 LBP"`.
 *
 * An unknown currency code falls back to exponent 0 rather than throwing: a new corridor's
 * currency should render as a slightly wrong-looking number, not blank out a Merchant's
 * order list.
 */
export function formatMinor(minor: string, currency: string): string {
  const exponent = EXPONENT[currency] ?? 0;
  const negative = minor.startsWith('-');
  const digits = (negative ? minor.slice(1) : minor).padStart(exponent + 1, '0');

  const whole = digits.slice(0, digits.length - exponent) || '0';
  const fraction = exponent > 0 ? digits.slice(digits.length - exponent) : '';

  const body = fraction === '' ? group(whole) : `${group(whole)}.${fraction}`;
  return `${negative ? '-' : ''}${body} ${currency}`;
}

/**
 * A cash count as typed, in minor units, or `null` when it is not an amount in that currency (P2-7).
 *
 * The cash desk takes a count in the units a person counts in, because a field asking for cents
 * invites typing 40 for forty dollars, a hundredfold short count the screen would then report as a
 * shortfall. So `40.50` is 4050 for USD, and LBP and SYP take whole pounds, as `EXPONENT` says.
 *
 * - **String arithmetic only** (I2): the digits are concatenated, never multiplied.
 * - **Digits and one point, nothing else.** A comma is refused rather than read, because it is a
 *   thousands separator in one language and a decimal one in another, and a count is the last place
 *   to guess which. More decimals than the currency has are refused for the same reason.
 * - **At most 18 digits**, the contract's own bound on a minor-unit string.
 */
export function majorToMinor(typed: string, currency: string): string | null {
  const exponent = EXPONENT[currency] ?? 0;
  const match = /^(\d+)(?:\.(\d+))?$/u.exec(typed.trim());
  if (!match) return null;
  const whole = match[1] ?? '';
  const fraction = match[2] ?? '';
  if (fraction.length > exponent) return null;
  const minor = `${whole}${fraction.padEnd(exponent, '0')}`.replace(/^0+(?=\d)/u, '');
  return minor.length > 18 ? null : minor;
}

/** `counted - expected` for two minor-unit strings, with `BigInt`: negative is short, positive is over. */
export function differenceMinor(expected: string, counted: string): string {
  return (BigInt(counted) - BigInt(expected)).toString();
}

/** A date the way both an Arabic and a French reader can copy it into a support ticket. */
export function formatInstant(iso: string, locale: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(`${locale}-u-nu-latn`, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
}
